import {ConfigService, RunConfigService, storage} from '@nuskin/ns-util';
import {UserService} from '@nuskin/ns-account';
import PickupUtil from "../shipping/pickupUtil"
import ServiceUtils from '../ServiceUtils';
import Adr from './adr';
import _ from 'lodash';

const ADR_ROOT_PATH = '/shop-service/api/v2/adr';

/**
 * Promise representing the currently in-progress server request for the ADR
 * list.
 * This is done to prevent multiple simultaneous calls for the list from
 * generating multiple server requests.
 * Null if no server request is currently in progress.
 * @type {null | Promise<Array<Adr>>}
 */
let adrListPromise;

let adrListData;

/**
 * Promise representing the currently in-flight server request for the ADR
 * status. This is done to prevent multiple simultaneous calls from generating
 * redundant server requests.
 * Null if no server request is currently in flight.
 * @type {null | Promise<ADRMemberStatusResponse>}
 */
let adrStatusPromise;

let adrStatusData;

/**
 *
 * AdrService
 *
 * @namespace AdrService
 */
export default {
    initAdrDraft,
    getAdrDraft,
    setAdrDraft,
    deleteAdrDraft,
    getAdrList,
    getAdrDetails,
    addAdrItems,
    updateAdrItems,
    removeAdrItems,
    setAdrShippingAddress,
    setAdrIsPrimary,
    setAdrDetailOnStorage,
    buildUpdateScanCardsList,
    updateAdrScanCards,
    editAdrScanCards,
    editAdrPayments,
    deleteAdr,
    isAdrOverride,
    getAdrOverrideId,
    isAdrOneTimeOrder,
    getAdrOneTimeItems,
    getAdrOrderReason,
    clearAdrOverride,
    getAdrStatus,
    getAdrPoints,
    clearLocalData,
    clearLocalAdrList,
    saveAdrId,
    editAdrItems,
    mapAdrDetail,
    setRedeemPointsChecked,
    getRedeemPointsChecked,
    updateAdrLineItems,
    getAdrMonths,
    updateGiftCertificate,
    editAdrLoyaltyRedemption
};

// =================================
//
// PUBLIC FUNCTIONS
//
// =================================

/**
 * Creates a new ADR draft object in local storage if one does not currently exist.
 * ADR Draft functions are used to maintain state when creating/editing a new ADR order.
 * @memberof AdrService
 * @example
 * AdrService.initAdrDraft()
 * @returns {Adr}
 */
function initAdrDraft() {
    let adr = getAdrDraft();
    if (!adr) {
        adr = new Adr({shipImmediate: true});
    }
    adr.fromCart = true;
    setAdrDraft(adr);
    return adr;
}

/**
 * Retrieves the current ADR draft from local storage.
 * Returns null if there no ADR draft is found.
 * ADR Draft functions are used to maintain state when creating/editing a new ADR order.
 * @memberof AdrService
 * @example
 * AdrService.getAdrDraft()
 * @returns {Adr}
 */
function getAdrDraft() {
    let data = storage.getItem(storage.metadata.ADR_DRAFT);
    return data ? new Adr(data) : null;
}

/**
 * Saves the given ADR draft to local storage, overwriting the previous draft if present.
 * ADR Draft functions are used to maintain state when creating/editing a new ADR order.
 * @memberof AdrService
 * @example
 * AdrService.setAdrDraft(adr)
 * @param {Adr} adr ADR draft
 */
function setAdrDraft(adr) {
    let data = adr instanceof Adr ? adr.toJSON() : adr;
    storage.setItem(storage.metadata.ADR_DRAFT, data);
}

/**
 * @memberof AdrService
 * @example
 * AdrService.deleteAdrDraft()
 * Clears the current ADR draft from local storage.
 * ADR Draft functions are used to maintain state when creating/editing a new ADR order.
 */
function deleteAdrDraft() {
    storage.removeItem(storage.metadata.ADR_DRAFT);
}

/**
 * Retrieves the user's list of ADRs in the current market.
 * @memberof AdrService
 * @example
 * AdrService.getAdrList(options)
 * @param {Object} [options]
 * @param {boolean} [options.forceRefresh = false] - request data from server instead of local storage
 * @param {string} [options.context] - request context (e.g. "cart", "manage")
 * @returns {Promise.<Array.<Adr>>} Promise that resolves to array of ADR Details
 */
async function getAdrList(options) {
    const DEFAULT_OPTIONS = {
        forceRefresh: false,
        context: null
    };
    options = Object.assign({}, DEFAULT_OPTIONS, options);

    let country = RunConfigService.getRunConfig().country;

    let data = getAdrListDataFromLocal();

    if (data.user === getUser().id && data.country === country && data.fullList && !options.forceRefresh) {
        return data.list;
    }

    if (!adrListPromise) {
        adrListPromise = getAdrListFromServer(options)
        // Reset adrListPromise regardless of whether the request was successful
            .then(list => {
                adrListPromise = null;
                return list;
            })
            .catch(err => {
                adrListPromise = null;
                throw err; // Propagate the error onward
            });
    }

    return adrListPromise;
}

/**
 * Retrieves the details of a single ADR for the current user.
 * @memberof AdrService
 * @example
 * AdrService.getAdrDetails(adrId, options)
 * @param {string} adrId - ID of the ADR
 * @param {Object} [options]
 * @param {boolean} [options.forceRefresh = true] - request data from server instead of local storage
 * @param {boolean} [options.reprice = false] - request data from server instead of local storage
 * @param {boolean} [options.fullAdrDetail = false] - request full ADR detail
 * @returns {Promise.<ADRDetail>} Promise that resolves to updated ADR Detail
 */
async function getAdrDetails(adrId, options) {
    const DEFAULT_OPTIONS = {
        forceRefresh: true,
        reprice: false,
        fullAdrDetail: false
    };
    options = Object.assign({}, DEFAULT_OPTIONS, options);

    let adr = getAdrDetailFromLocal(adrId);

    if (!adr || options.forceRefresh || _isCachedAdrComplete(adr, options)) {
        adr = await getAdrDetailFromServer(adrId);
    }

    return adr;
}

/**
 * Adds the given items to the ADR
 * @memberof AdrService
 * @example
 * AdrService.addAdrItems(adrId, items, options)
 * @param {string} adrId - ID of the ADR
 * @param {Object[]} items - the items to be added to the ADR:
 * @param {string} items[].SKU - item's SKU ID
 * @param {number} items[].RequestedQuantity - item's quantity
 * @param {boolean} items[].OneTime - whether item is one-time only
 * @param {Object} [options] - additional options:
 * @param {string} [options.ProductDiscountCode] - product discount code for this ADR
 * @param {string} [options.ShippingDiscountCode] - shipping discount code for this ADR
 * @returns {Promise.<ADRDetail>} Promise that resolves to updated ADR Detail
 */
async function addAdrItems(adrId, items, options) {
    return editAdrItems(adrId, items, 'ADD', options);
}

/**
 * Updates the given items' entries in the ADR
 * @memberof AdrService
 * @example
 * AdrService.updateAdrItems(adrId, items, options)
 * @param {string} adrId - ID of the ADR
 * @param {Object[]} items - the ADR items to be updated:
 * @param {number} items[].LineID - item's Line ID
 * @param {string} [items[].SKU] - new SKU for the item
 * @param {number} [items[].RequestedQuantity] - new requested quantity
 * @param {boolean} [items[].OneTime] - whether item is a one-time order
 * @param {Object} [options] - additional options:
 * @param {string} [options.ProductDiscountCode] - product discount code for this ADR
 * @param {string} [options.ShippingDiscountCode] - shipping discount code for this ADR
 * @returns {Promise.<ADRDetail>} Promise that resolves to updated ADR Detail
 */
async function updateAdrItems(adrId, items, options) {
    return editAdrItems(adrId, items, 'UPDATE', options);
}

/**
 * Removes the given items from the ADR
 * @memberof AdrService
 * @example
 * AdrService.removeAdrItems(adrId, items, options)
 * @param {string} adrId - ID of the ADR
 * @param {Object[]} items - the items to be removed from the ADR:
 * @param {number} items[].LineID - item's Line ID
 * @param {Object} [options] - additional options:
 * @param {string} [options.ProductDiscountCode] - product discount code for this ADR
 * @param {string} [options.ShippingDiscountCode] - shipping discount code for this ADR
 * @returns {Promise.<ADRDetail>} Promise that resolves to updated ADR Detail
 */
async function removeAdrItems(adrId, items, options) {
    return editAdrItems(adrId, items, 'DELETE', options);
}

/**
 * Sets the ADR's shipping address to the information given.
 * The shipping address provided must follow a similar structure to the example below:
 * <pre>
 * {
 *   Name: "John Doe",
 *   Address: {
 *     Street1: "75 W Center St",
 *     City: "Provo",
 *     District: "",
 *     Region: "UT",
 *     PostalCode: "84601-4432",
 *     CountryCode: "US"
 *   },
 *   Contact: [{
 *     Country: "US",
 *     Value: {
 *       value: "801-555-1234",
 *       Type: "HOMEPHONE"
 *     }
 *   }],
 *   Instructions: ""
 * }</pre>
 *
 * Address fields permitted include the following:
 * Street1, Street2, City, District, Region, PostalCode, CountryCode
 *
 * Contact entries can include any of the following types:
 * WORKPHONE, HOMEPHONE, MOBILEPHONE, FAX, EMAIL
 *
 * @memberof AdrService
 * @example
 * AdrService.setAdrShippingAddress(adrId, shippingAddress, options)
 * @param {string} adrId - ID of the ADR
 * @param {Object} shippingAddress - shipping address information
 * @param {string} shippingAddress.Name - shipping name
 * @param {Object} shippingAddress.Address - shipping address
 * @param {Object[]} shippingAddress.Contact - shipping contact information
 * @param {string} shippingAddress.Instructions - shipping instructions
 * @param {Object} [options] - optional parameters:
 * @param {string} [options.ShippingMethod] - shipping method code
 * @param {string} [options.PickUpLocation] - pickup location code; only applicable if shipping method is Pickup Location ("PU")
 * @param {number} [options.OrderFrequency] - order frequency
 * @param {number} [options.DeliveryDate] - next delivery date
 * @param {Array<Custom>} [options.Custom] - custom data fields to include in request
 * @returns {Promise.<ADRDetail>} Promise that resolves to updated ADR Detail
 */
async function setAdrShippingAddress(adrId, shippingAddress, options) {
    let user = getUser();

    let adr = await getAdrDetails(adrId, options).then((adr) => {

        const DEFAULT_OPTIONS = {
            ShippingMethod: adr.SalesOrderDetail.Shipping.ShippingMethod.Code,
            PickUpLocation: adr.SalesOrderDetail.Shipping.PickUpLocation,
            OrderFrequency: adr.OrderFrequency,
            DeliveryDate: adr.NextDeliveryDate,
            Custom: [],
            Simulate: false
        };
        options = Object.assign({}, DEFAULT_OPTIONS, options);

        let body = createBasicAdrPutBody(adr, options);

        // If no shipping address is passed, do not create the shipping party body
        if (shippingAddress) {
            body.adr.SalesOrder.Shipping = {
                ShippingParty: [{
                    ShippingMemberID: user.id,
                    Type: 'SHIP_TO',
                    ShippingAddress: shippingAddress
                }],
                ShippingMethod: {
                    Code: options.ShippingMethod
                }
            };

            // If shipping method is Pickup, add pickup location to the request body
            if (options.ShippingMethod === 'PU') {
                body.adr.SalesOrder.Shipping.ShippingMethod.PickUpLocation = options.PickUpLocation;
            }

            // If shipping methis is 'K1' and pick up option has been selected, add the selected
            // pick up option to the adr request body as another item in the ShippingParty array.
            if (PickupUtil.supports(options.ShippingMethod) && options.pickUpOptionSelected) {
                body.adr.SalesOrder.Shipping.ShippingParty.push(options.pickUpOptionSelected);
            }
        }

        body.adr.OrderFrequency = options.OrderFrequency;
        body.adr.DeliveryDate = JSON.stringify(options.DeliveryDate);
        body.adr.SalesOrder.Custom = options.Custom;

        return makeAdrDetailPutRequest(adrId, body);
    }).catch(err => { throw err;});
    return adr;
}

/**
 * Sets whether this ADR is the user's primary ADR
 * @memberof AdrService
 * @example
 * AdrService.setAdrIsPrimary(adrId, isPrimary)
 * @param {string} adrId - ID of the ADR
 * @param {boolean} isPrimary - whether the ADR should be made primary
 * @returns {Promise.<ADRDetail>} Promise that resolves to updated ADR Detail
 */
async function setAdrIsPrimary(adrId, isPrimary) {
    let adr = await getAdrDetails(adrId, {});
    let customFields = [{
        Key: 'ZZPRIMARY',
        Type: '',
        Value: isPrimary ? 'X' : ''
    }];

    let body = createBasicAdrPutBody(adr);
    body.adr.SalesOrder.Custom = customFields;

    return makeAdrDetailPutRequest(adrId, body);
}

/**
 * Adds, modifies, or deactivates scan cards associated with this ADR
 * @memberof AdrService
 * @example
 * AdrService.updateAdrScanCards(adrId, cards, options)
 * @param {string} adrId - ID of the ADR
 * @param {Object[]} scans - array of scan cards to add, modify, or deactivate
 * @param {string} scans[].ScanNumber - the scan card's ID number
 * @param {string} scans[].Label - the scan card's label
 * @param {boolean} scans[].Active - whether scan card is active
 * @param {string} scans[].Action - ADD, CHANGE, DEACTIVATE
 * @param {Object} [options] - additional options:
 * @param {boolean} [options.forceRefresh = false] - request data from server instead of local storage
 * @param {number} [options.ContractLength] - ADR contract length
 * @returns {Promise.<ADRDetail>} Promise that resolves to updated ADR Detail
 */
async function editAdrScanCards(adrId, scans, options) {
    options = Object.assign({}, options);
    let adr = await getAdrDetails(adrId, options);

    let body = createBasicAdrPutBody(adr);

    if (scans[0]) {
        scans.forEach(card => { card.ADRNumber = adr.SalesOrderDetail.OrderID; });
        body.adr.Scan = scans;
    } else {
        scans.ADRNumber = adr.SalesOrderDetail.OrderID;
        body.adr.Scan = [scans];
    }

    if (typeof options.ContractLength === 'number') {
        body.adr.ContractLength = options.ContractLength;
    }

    return makeAdrDetailPutRequest(adrId, body);
}

/**
 * Build list of scan card request objects for PUT request
 * @param {Object[]} scanCards
 * @returns {Object[]}
 */
function buildUpdateScanCardsList (scanCards) {
    return scanCards.map((card) => {
        return {
            ScanNumber: card.ScanNumber,
            Label: card.Label,
            Active: card.Active,
            Action: card.Action
        };
    });
}

/**
 * Adds, modifies, or deactivates scan cards associated with this ADR
 * @param {string} adrId - ID of the ADR
 * @param {Object[]} cards - array of scan cards to add, modify, or deactivate
 * @param {string} cards[].ScanNumber - the scan card's ID number
 * @param {string} cards[].Label - the scan card's label
 * @param {boolean} cards[].Active - whether scan card is active
 * @param {string} cards[].Action - ADD, CHANGE, DEACTIVATE
 * @param {Object} [options] - additional options:
 * @param {number} [options.ContractLength] - defaults to ADR's current contract length
 * @returns {Promise.<ADRDetail>} Promise that resolves to updated ADR Detail
 */
async function updateAdrScanCards(adrId, cards, options) {
    let adr = await getAdrDetails(adrId);
    const DEFAULT_OPTIONS = {
        ContractLength: adr.ContractLength
    };
    options = Object.assign({}, DEFAULT_OPTIONS, options);

    cards.forEach(card => {
        card.ADRNumber = adr.SalesOrderDetail.OrderID;
    });

    let body = createBasicAdrPutBody(adr);
    body.adr.Scan = cards;
    body.adr.ContractLength = options.ContractLength;

    return makeAdrDetailPutRequest(adrId, body);
}

/**
 * Deletes the ADR corresponding to the given ID from the server
 * Local ADR data is cleared out on successful deletion to prevent future retrieval of stale data.
 *
 * @memberof AdrService
 * @example
 * AdrService.deleteAdr(adrId)
 * @param {string} adrId - ID of the ADR
 * @returns {Promise} Promise that resolves on successful deletion
 */
async function deleteAdr(adrId) {
    let user = getUser();
    let country = RunConfigService.getRunConfig().country;
    let language = RunConfigService.getRunConfig().language;
    let url = `${getBaseUrl()}/${adrId}?eid=${user.eid}&countryCode=${country}&sapId=${user.id}&locale=${language}_${country}`;

    let response = await fetch(url, {
        method: 'DELETE',
        headers: getHeaders(),
        credentials: 'omit'
    });

    let transformedResponse = ServiceUtils.transformFetchResponse(response)
        .catch(err => { throw err; });

    deleteAdrDetailLocal(adrId);
    deleteAdrStatusDataLocal();

    return transformedResponse;
}

/**
 * Returns true if the current cart represents an ADR Override or ADR One-Time order.
 * @memberof AdrService
 * @example
 * AdrService.isAdrOverride()
 * @returns {boolean}
 */
function isAdrOverride() {
    let data = getAdrOverrideId();
    return !!data && data !== 'null';
}

/**
 * Returns the ADR ID of the current ADR Override or ADR One-Time order.
 * Returns null if the current order is of neither type.
 * @memberof AdrService
 * @example
 * AdrService.getAdrOverrideId()
 * @returns {string}
 */
function getAdrOverrideId() {
    return storage.getItem(storage.metadata.ADR_OVERRIDE_ID);
}

/**
 * Returns true if the current cart represents an ADR One-Time order.
 * @memberof AdrService
 * @example
 * AdrService.isAdrOneTimeOrder()
 * @returns {boolean}
 */
function isAdrOneTimeOrder() {
    let oneTimeItems = getAdrOneTimeItems();
    return isAdrOverride() && !!oneTimeItems && oneTimeItems !== 'null';
}

/**
 * Returns the list of items associated with the current ADR One-Time order.
 * Returns null if the current order is not an ADR One-Time order.
 * @memberof AdrService
 * @example
 * AdrService.getAdrOneTimeItems()
 * @returns {Object[]}
 */
function getAdrOneTimeItems() {
    return storage.getItem(storage.metadata.ADR_ONE_TIME_ITEMS);
}

/**
 * Returns the Order Reason for the current ADR Override or ADR One-Time order.
 * Returns null if the current order is of neither type.
 * @memberof AdrService
 * @example
 * AdrService.getAdrOrderReason()
 * @returns {string}
 */
function getAdrOrderReason() {
    return storage.getItem(storage.metadata.ORDER_REASON);
}

/**
 * Clears ADR Override and ADR One-Time order data from local storage.
 * @memberof AdrService
 * @example
 * AdrService.clearAdrOverride()
 */
function clearAdrOverride() {
    storage.removeItem(storage.metadata.ADR_OVERRIDE_ID);
    storage.removeItem(storage.metadata.ORDER_REASON);
    storage.removeItem(storage.metadata.ADR_ONE_TIME_ITEMS);
}

/**
 * Retrieves the user's ADR Status data for the current market
 * @memberof AdrService
 * @example
 * AdrService.getAdrStatus(options)
 * @param {Object} [options] - options:
 * @param {boolean} [options.forceRefresh = false] - request data from server instead of local storage
 * @returns {Promise.<ADRMemberStatusResponse>} Promise that resolves to ADR Status object
 */
async function getAdrStatus(options) {
    const DEFAULT_OPTIONS = {
        forceRefresh: false
    };
    options = Object.assign({}, DEFAULT_OPTIONS, options);

    let data = getAdrStatusDataFromLocal();
    if (data.user === getUser().id && data.status && !options.forceRefresh) {
        return data.status;
    }

    if (!adrStatusPromise) {
        adrStatusPromise = getAdrStatusFromServer().finally(() => {
            // Clear cached promise to allow future server requests to go through.
            adrStatusPromise = null;
        });
    }

    return adrStatusPromise;
}

/**
 * Retrieves the user's available ADR points for the current market
 * @memberof AdrService
 * @example
 * AdrService.getAdrPoints(options)
 * @param {Object} [options] - options:
 * @param {boolean} [options.forceRefresh = false] - request data from server instead of local storage
 * @returns {Promise.<number>} Promise that resolves to the user's available points
 */
async function getAdrPoints(options) {
    const DEFAULT_OPTIONS = {
        forceRefresh: false
    };
    options = Object.assign({}, DEFAULT_OPTIONS, options);

    let status = await getAdrStatus(options);

    if (status.ADRPoints) {
        return status.ADRPoints.map(mkt => mkt.AvailablePoints || 0)
            .reduce((a, b) => a+b, 0);
    } else {
        return 0;
    }
}

/**
 * Clears the user's ADR information from local storage
 * @memberof AdrService
 * @example
 * AdrService.clearLocalData()
 */
function clearLocalData() {
    deleteAdrDraft();
    clearAdrOverride();
    deleteAdrStatusDataLocal();
    deleteAdrListDataLocal();
}

/**
 * Clears the user's ADR List from local storage
 * @memberof AdrService
 */
function clearLocalAdrList() {
    deleteAdrListDataLocal();
}

// =================================
//
// PRIVATE FUNCTIONS
//
// =================================

/**
 * Retrieves the user's ADR List from the server
 * @param {*} [options] - optional parameters
 * @ignore
 * @returns {Promise.<Array.<ADRDetail>>} ADR Detail List
 */
async function getAdrListFromServer(options = {}) {
    let user = getUser();
    let country = RunConfigService.getRunConfig().country;
    let language = RunConfigService.getRunConfig().language;
    let url = `${getBaseUrl()}/list/${user.id}?eid=${user.eid}&countries=${country}&locale=${language}_${country}`;

    if (options.context) url += `&context=${options.context}`;

    let response = await fetch(url, {
        method: 'GET',
        headers: getHeaders(),
        credentials: 'omit'
    });

    let data = await ServiceUtils.transformFetchResponse(response)
        .catch(err => { throw err; });

    if (!data || !data.ADRListResponse || !data.ADRListResponse.ADR) {
    // Data returned by server is malformed
        throw new Error('Malformed ADR List response from server');
    }

    setAdrListDataLocal({
        user: user.id,
        country,
        list: data.ADRListResponse.ADR,
        fullList: true
    });

    return data.ADRListResponse.ADR;
}

/**
 * Retrieves the user's ADR list data from local storage, if present. Otherwise returns an empty object.
 * @returns {{user: String, list: Array<Adr>, fullList: Boolean}}
 */
function getAdrListDataFromLocal() {
    if (adrListData) return adrListData;
    return storage.getItem(storage.metadata.ADR_LIST) || {};
}

/**
 * Sets the user's ADR list data to local storage.
 * @param {*} data
 */
function setAdrListDataLocal(data) {
    adrListData = data;
    storage.setItem(storage.metadata.ADR_LIST, data);
}

/**
 * Removes the user's ADR list data from local storage.
 */
function deleteAdrListDataLocal() {
    adrListData = null;
    storage.removeItem(storage.metadata.ADR_LIST);
}

/**
 * Retrieves the ADR Details from the server for the given ADR ID
 * @ignore
 * @param adrId {String} ID of the ADR requested
 * @returns {Promise.<ADRDetail>} ADR Detail object
 */
async function getAdrDetailFromServer(adrId) {
    let user = getUser();
    let country = RunConfigService.getRunConfig().country;
    let language = RunConfigService.getRunConfig().language;
    let url = `${getBaseUrl()}/${adrId}?eid=${user.eid}&countryCode=${country}&locale=${language}_${country}`;

    let response = await fetch(url, {
        method: 'GET',
        headers: getHeaders(),
        credentials: 'omit'
    });

    let data = await ServiceUtils.transformFetchResponse(response)
        .catch(err => { throw err; });

    if (!data || !data.ADRResponse || !data.ADRResponse.ADRDetail) {
    // Data returned by server is malformed
        throw new Error('Malformed ADR Detail response from server');
    }
    setAdrDetailLocal(data.ADRResponse.ADRDetail);

    return data.ADRResponse.ADRDetail;
}

/**
 * Retrieves the ADR Details from local storage for the given ADR ID.
 * Returns null if the ADR is not found.
 * @ignore
 * @param {String} adrId - ID of the ADR requested
 * @returns {ADRDetail} ADR Detail object
 */
function getAdrDetailFromLocal(adrId) {
    let data = getAdrListDataFromLocal();
    if (data.user === getUser().id && data.list) {
        return data.list.find(adr => adr.SalesOrderDetail.OrderID === adrId) || null;
    }

    return null;
}

/**
 * Removes the ADR matching the given ID from local storage
 * @ignore
 * @param {string} adrId - ID of the ADR
 */
function deleteAdrDetailLocal(adrId) {
    let data = getAdrListDataFromLocal();
    if (data.user === getUser().id && data.list) {
        let idx = data.list.map(adr => adr.SalesOrderDetail.OrderID)
            .indexOf(adrId);
        if (idx > -1) {
            data.list.splice(idx, 1);
            setAdrListDataLocal(data);
        }
    }
}

/**
 * Saves the ADR Details object to local storage
 * @ignore
 * @param {ADRDetail} adr - ADR Details object
 */
function setAdrDetailLocal(adr) {
    let data = getAdrListDataFromLocal();
    if (data.user === getUser().id && data.list) {
        let idx = data.list.map(adr => adr.SalesOrderDetail.OrderID)
            .indexOf(adr.SalesOrderDetail.OrderID);
        if (idx > -1) {
            data.list[idx] = adr;
        } else {
            data.list.push(adr);
        }
        setAdrListDataLocal(data);
    } else {
        setAdrListDataLocal({
            user: getUser().id,
            country: RunConfigService.getRunConfig().country,
            list: [adr],
            fullList: false
        });
    }
}

/**
 * Makes an edit to the given item(s) in the given ADR.
 * This is used for ADD, UPDATE, and DELETE operations for ADR items.
 * @ignore
 * @param {String} adrId - ID of the ADR
 * @param {Object[]} items - the items to be edited in the ADR:
 * @param {string} items[].SKU - item's SKU ID
 * @param {number} items[].RequestedQuantity - item's quantity
 * @param {boolean} items[].OneTime - whether item is one-time only
 * @param {string} action - edit action to perform (ADD, UPDATE, DELETE)
 * @param {Object} [options] - additional options:
 * @param {string} [options.ProductDiscountCode] - product discount code for this ADR
 * @param {string} [options.ShippingDiscountCode] - shipping discount code for this ADR
 * @returns {Promise.<ADRDetail>} updated ADR Detail object
 */
async function editAdrItems(adrId, items, action, options) {
    let adr = await getAdrDetails(adrId, {});

    const DEFAULT_OPTIONS = {
        ProductDiscountCode: adr.SalesOrderDetail.ProductDiscountCode,
        ShippingDiscountCode: adr.SalesOrderDetail.ShippingDiscountCode
    };
    options = Object.assign({}, DEFAULT_OPTIONS, options);

    items = items.map(item => Object.assign({Action: action}, item));

    let body = createBasicAdrPutBody(adr, options);
    body.adr.SalesOrder.ProductDiscountCode = options.ProductDiscountCode;
    body.adr.SalesOrder.ShippingDiscountCode = options.ShippingDiscountCode;
    if (options.Scan) body.adr.Scan = options.Scan;
    body.adr.SalesOrder.Item = items;

    return makeAdrDetailPutRequest(adr.SalesOrderDetail.OrderID, body);
}

/**
 * Makes a PUT request for the given ADR. This is used for ADR edits.
 * @ignore
 * @param {string} adrId - ID of the ADR
 * @param {Object} body - PUT request body
 * @returns {Promise.<ADRDetail>} updated ADR Detail
 */
async function makeAdrDetailPutRequest(adrId, body) {
    let user = getUser();
    let country = RunConfigService.getRunConfig().country;
    let language = RunConfigService.getRunConfig().language;
    let url = `${getBaseUrl()}/${adrId}?eid=${user.eid}&countryCode=${country}&locale=${language}_${country}`;
    let headers = getHeaders();
    headers['content-type'] = 'application/json';

    let response = await fetch(url, {
        method: 'PUT',
        headers: headers,
        credentials: 'omit',
        body: JSON.stringify(body)
    });

    let data = await ServiceUtils.transformFetchResponse(response)
        .catch(err => { throw err; });

    if (!(data.AdrResponse && data.AdrResponse.ADRDetail)) {
    // Data returned by server is malformed
        let errorMsg = 'Malformed ADR Detail PUT response from server';
        throw new Error(errorMsg);
    }

    setAdrDetailLocal(data.AdrResponse.ADRDetail);
    deleteAdrStatusDataLocal();   // Clear ADR Status Local to get updated points balance

    return data.AdrResponse.ADRDetail;
}

/**
 * Retrieves the user's ADR Status from the server
 * @ignore
 * @returns {Promise.<ADRMemberStatusResponse>} Promise that resolves to ADR Status object
 */
async function getAdrStatusFromServer() {
    let user = getUser();
    let country = RunConfigService.getRunConfig().country;
    let language = RunConfigService.getRunConfig().language;
    let url = `${getBaseUrl()}/status/${user.id}?eid=${user.eid}&countryGroup=${country}&locale=${language}_${country}`;

    let response = await fetch(url, {
        method: 'GET',
        headers: getHeaders(),
        credentials: 'omit'
    });

    let data = await ServiceUtils.transformFetchResponse(response)
        .catch(err => { throw err; });

    if (!data.ADRMemberStatusResponse) {
    // Data returned by server is malformed
        throw new Error('Malformed ADR Status response from server');
    }

    setAdrStatusDataLocal({
        user: user.id,
        status: data.ADRMemberStatusResponse
    });

    return data.ADRMemberStatusResponse;
}

/**
 * Retrieves the user's ADR Status from local storage.
 * Returns null if the Status object is not found.
 * @ignore
 * @returns {{user: String, status: ADRMemberStatusResponse}}
 */
function getAdrStatusDataFromLocal() {
    if (adrStatusData) return adrStatusData;
    return storage.getItem(storage.metadata.ADR_STATUS) || {};
}

/**
 * Saves the ADR Status object to local storage
 * @ignore
 * @param {*} data - ADR Status data
 */
function setAdrStatusDataLocal(data) {
    adrStatusData = data;
    storage.setItem(storage.metadata.ADR_STATUS, data);
}

/**
 * Saves the ADR Detail object to local storage
 * @ignore
 * @param {*} detail - ADR Detail object
 */
function setAdrDetailOnStorage(detail) {
    storage.setItem(storage.metadata.ADR_DETAIL, detail);
}

/**
 * Removes the user's ADR Status object from local storage
 * @ignore
 */
function deleteAdrStatusDataLocal() {
    adrStatusData = null;
    storage.removeItem(storage.metadata.ADR_STATUS);
}

/**
 * Returns the User object for the current authenticated user
 * @ignore
 * @returns {User}
 */
function getUser() {
    let user = UserService.getUser();
    if (!user) {
        throw new Error('User not authenticated');
    }
    return user;
}

/**
 * Creates the body object needed for an ADR PUT request.
 * Other functions use this object as a starting point for ADR edits.
 * @ignore
 * @param {ADRDetail} adr - ADR the request is created for
 * @param {Object} options - any Additional options for ADR
 * @returns {Object} request body object
 */
function createBasicAdrPutBody(adr, options = {}) {
    let user = getUser();
    let country = RunConfigService.getRunConfig().country;

    return {
        adr: {
            SalesOrder: {
                OrderAction: options.Simulate ? 'SIMULATE' : 'MANAGE',
                Reason: 'STANDARD',
                CountryCode: country,
                SoldToMemberID: user.id
            },
            timestamp: adr.TimeStamp,
            OrderFrequency: adr.OrderFrequency,
            DeliveryDate: adr.DeliveryDate
        },
        doc_number: adr.SalesOrderDetail.OrderID
    };
}

/**
 * Sets the ADR's payment to the information given.
 * The payment provided must follow a similar structure to the example below:
 * <pre>
 * [
 *   {
 *     CreditCard: {CardNumber: "tkcP8d70Sc2jm5RdN7XaXd4w-1111", ExpirationMonth: "11", ExpirationYear: "2022", Name: "sg", PostalCode: null, …}
 *     action: "ADD"
 *     amount: "0.0"
 *     type: "CCARD"
 *   },
 *   {
 *     CreditCard: {Type: "MC", CardNumber: "tVD8xAvL32eu-ZSECgm988qs-0007", AuthorizationCode: "", Name: "madhavi", ExpirationMonth: "07", …}
 *     action: "DELETE"
 *     id: "000001"
 *   }
 * ]
 * </pre>
 *
 * Payment fields permitted include the following:
 * CreditCard, action, amount, type, Custon
 *
 * @memberof AdrService
 * @example
 * AdrService.editAdrPayments(adrId, payments, options)
 *
 * @ignore
 * @param {string} adrId - ID of the ADR
 * @param {Object[]} payments - array of payment data objects to edit
 * @returns {Promise.<ADRDetail>} Promise that resolves to updated ADR Detail
 */
async function editAdrPayments(adrId, payments, options) {
    let adr = await getAdrDetails(adrId, options);
    let body = createBasicAdrPutBody(adr);
    body.adr.SalesOrder.Payment = payments;
    await makeAdrDetailPutRequest(adrId, body);
    return getAdrDetails(adrId, {});
}

async function editAdrLoyaltyRedemption(adrId, loyaltyAmount, currency, options) {
    let adr = await getAdrDetails(adrId, options);
    let body = createBasicAdrPutBody(adr);

    if (loyaltyAmount === undefined || loyaltyAmount < 0) loyaltyAmount = 0;
    if (!body.adr.SalesOrder.Pricing) body.adr.SalesOrder.Pricing = [];

    let loyaltyPrice = {
        Type: 'POINTS_USED',
        Rate: loyaltyAmount,
        Currency: currency,
        Unit: null,
        PricingUnit: 0,
        BaseValue: 0,
        Value: 0,
        TaxCode: null
    };

    body.adr.SalesOrder.Pricing.push(loyaltyPrice)

    await makeAdrDetailPutRequest(adrId, body);
    return await getAdrDetails(adrId, {});
}

function getBaseUrl() {
    return ConfigService.getMarketConfig().siteUrl + ADR_ROOT_PATH;
}

function getHeaders() {
    let config = ConfigService.getMarketConfig();
    return {
        'client_id': config.checkout.clientId,
        'client_secret': config.checkout.clientSecret,
        'Accept': 'application/json'
    };
}

/**
 * Saves a currently selected ADR's ID
 * @ignore
 * @param {*} id
 */
function saveAdrId (id) {
    storage.setItem(storage.metadata.ADR_CURRENT_ID, id);
}

/**
 * Maps adr detail data to be consumable by ns-apd-wrapper
 * @ignore
 * @param {*} adr
 */
function mapAdrDetail (adrResponse, newAdr, shippingAvailableMethods, shippingMethod, pickupLocation) {
    let adrId = adrResponse.SalesOrderDetail.OrderID;
    let adr = {};
    adr = updateAdrLineItems(adrResponse);
    adr.countryCode = adrResponse.SalesOrderDetail.CountryCode;
    adr.mergedProducts = _.merge(adr.lineItems, newAdr.lineItems);
    adr.value = newAdr.value;
    adr.key = newAdr.key;
    adr.adrName = newAdr.adrName;
    adr.productSkus = newAdr.productSkus;
    adr.payment = adrResponse.SalesOrderDetail.Payment;

    const matchesMethod = item =>
        item.Code === shippingMethod
      && (!pickupLocation || item.PickUpLocation === pickupLocation);
    adr.shipping = {};
    adr.shipping.shippingMethods = adrResponse.SalesOrderDetail.ShippingAvailableMethods || shippingAvailableMethods;
    adr.shipping.shipping = adrResponse.SalesOrderDetail.Shipping;
    adr.shipping.shipping.ShippingMethod =
    adr.shipping.shippingMethods && adr.shipping.shippingMethods.some(matchesMethod)
        ? adr.shipping.shippingMethods.find(matchesMethod)
        : adrResponse.SalesOrderDetail.Shipping.ShippingMethod;

    adr.products = adr.mergedProducts.filter(item => !item.oneTime && !item.payWithPoints);
    adr.oneTimeProducts = adr.mergedProducts.filter(item => item.oneTime || item.payWithPoints);
    adr.OrderID = adrResponse.SalesOrderDetail.OrderID;
    adr.TimeStamp = adrResponse.TimeStamp;
    adr.Editable = adrResponse.Editable;
    adr.NextDeliveryDate = adrResponse.NextDeliveryDate;
    adr.OrderFrequency = adrResponse.OrderFrequency;
    adr.shippingTotal = adrResponse.SalesOrderDetail.OrderTotals.Shipping;
    adr.psvBalance = adrResponse.SalesOrderDetail.OrderTotals.PSV;
    adr.tax = adrResponse.SalesOrderDetail.OrderTotals.Tax;
    adr.total = adrResponse.SalesOrderDetail.OrderTotals.GrandTotal;
    adr.subTotal = adrResponse.SalesOrderDetail.OrderTotals.ItemSubtotal;
    adr.overridden = adrResponse.Overridden;
    adr.held = adrResponse.Held;
    adr.LastPlacedDate = adrResponse.LastPlacedDate;
    adr.Scan = adrResponse.Scan;
    adr.scanCardsCount = newAdr.scanCardsCount;
    adr.monthCount = adrResponse.MonthCount;
    //network response is spelling receipt incorrectly - easiest to just adjust the code than wait for another round.
    adr.giftReceipt = adrResponse.SalesOrderDetail.GiftReciept;
    adr.id = adrId;

    // These are the amount of loyalty points used. SAP keeps them as a negative number toward the total as it is a discount.
    let pointsUsed = adrResponse.SalesOrderDetail.OrderTotals.PointsUsed
    if(pointsUsed !== undefined) {
        adr.pointsUsed = pointsUsed >= 0 ? pointsUsed: pointsUsed * -1;
    }

    return adr;
}

/**
 * Sets the redeem points checked property in local storage - this is used by the checkbox on
 * ns-adp-cart.
 * @param {boolean} isChecked the status of the checkbox
 */
function setRedeemPointsChecked(isChecked){
    storage.setItem(storage.metadata.ADR_REDEEM_PTS_CHECKED, isChecked);
}

/**
 * Returns the redeem points checked property in local storage - this is used by the checkbox on
 * ns-adp-cart.
 * @return {Boolean} the value of the redeem points checked property.
 */
function getRedeemPointsChecked(){
    return !!storage.getItem(storage.metadata.ADR_REDEEM_PTS_CHECKED);
}

/**
 * Returns an array of objects with an Adr's skus and corresponding quantities
 * @ignore
 * @param {*} adr
 */
function updateAdrLineItems (adr) {
    let adrItem = {};
    adrItem['lineItems'] = [];
    adrItem['lineItemSkuList'] = [];
    adr.SalesOrderDetail.LineItemDetails.filter(item => item.LineItem.ParentID === 0).map((item) => {
        adrItem['key'] = adr.SalesOrderDetail.OrderID;
        adrItem['value'] = `ADR#${adr.SalesOrderDetail.OrderID}`;
        adrItem['lineItems'].push({
            'sku': item.LineItem.SKU,
            'requestedQuantity': item.LineItem.RequestedQuantity,
            'oneTime': item.LineItem.OneTime,
            'payWithPoints': item.LineItem.PayWithPoints,
            'lineId': item.LineItem.LineID,
            'parentId': item.LineItem.ParentID,
            'custom': item.LineItem.Custom
        });
        adrItem['lineItemSkuList'].push(item.LineItem.SKU);
    });
    return adrItem;
}

/**
 * Helper method to determine if cached ADR is complete.
 * Used by getAdrDetails(), if True method calls GET endpoint and returns full GET Details
 * object including the Shipping, and ShippingAvailableMethods.
 * This response is then cached for subsequent requests.
 * @ignore
 * @param {ADRDetail} adr - ADR object from cache
 * @param {Object} [options]
 * @param {boolean} [options.fullAdrDetail] - request full ADR details
 * @returns {boolean} - flag indicating whether the cached ADR is complete
 */
function _isCachedAdrComplete(adr, options) {
    return (options.fullAdrDetail && !adr.SalesOrderDetail.Shipping);
}

/**
 * returns month count from an adr stored in local
 * used by shopcontext to determine if ADR qualifies for WADWD discount
 * @param {string} adrId - ID of the ADR
 * @returns {number} the MonthCount property of the adr
 */
function getAdrMonths(adrId) {
    let adr = getAdrDetailFromLocal(adrId);
    return adr? adr.MonthCount : 0;
}

function updateGiftCertificate(adrId, updateCertificateValue) {
    return getAdrDetails(adrId, {})
        .then((adr) => {
            let body = createBasicAdrPutBody(adr);
            body.adr.SalesOrder.GiftReceipt = updateCertificateValue;
            return  makeAdrDetailPutRequest(adrId, body)
        }).catch(err => console.error(err, adrId))
}
